import React, { useState } from 'react';
import axios from 'axios';
import { apiService } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const LOGIN_URL = `${API_BASE.replace(/\/api\/?$/, '')}/api/admin/login`;

interface LoginProps {
  onSuccess: () => void;
  onGoToRegister?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSuccess, onGoToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(LOGIN_URL, {
        email: email.trim().toLowerCase(),
        password,
      });
      if (res.data?.success && res.data?.token) {
        localStorage.setItem('token', res.data.token);
        apiService.setAuthToken(res.data.token);
        onSuccess();
      } else {
        setError(res.data?.message || 'Login failed');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data
          ?.message ||
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error)?.message ||
        'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-xl shadow-xl p-8">
          <div className="flex flex-col items-center mb-4">
            <img
              src="/logo.png"
              alt=""
              className="flex-shrink-0 mb-3"
              style={{ maxHeight: '56px', width: 'auto', maxWidth: '160px', objectFit: 'contain' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <p className="text-lg font-semibold text-white text-center">Momentum Inverse Model</p>
            <h1 className="text-xl font-bold text-slate-100 text-center mt-1">
              Trading Automation
            </h1>
          </div>
          <p className="text-slate-400 text-sm text-center mb-6">
            Sign in to view the dashboard
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-slate-700/50 border border-slate-600 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Email"
                autoComplete="email"
                disabled={loading}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-slate-700/50 border border-slate-600 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Password"
                autoComplete="current-password"
                disabled={loading}
              />
            </div>
            {error && (
              <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          {onGoToRegister && (
            <p className="mt-4 text-center text-slate-500 text-sm">
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={onGoToRegister}
                className="text-blue-400 hover:text-blue-300 font-medium"
              >
                Register
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
